import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Toast from '../components/Toast';
import Combobox from '../components/Combobox';
import FilterableTable from '../components/FilterableTable';
import { filtreazaCantitate, normalizeazaNumar, formatCantitate } from '../utils/format';
import { formatNumarRaport, exportaCSV, printeazaTabel } from '../utils/export';
import { construiesteHtmlBonConsum } from '../utils/bonConsum';

const MOTIV_LABEL = {
  PRODUCTIE: 'Transfer productie', CASARE: 'Casare', PIERDERE: 'Pierdere', DISTRUGERE: 'Distrugere',
  VANZARE: 'Vanzare', CASARE_PIERDERE: 'Casare/Pierdere', RETUR: 'Retur in magazie',
  ANULARE_FINALIZARE: 'Anulare finalizare eronata'
};

const GOL = {
  itemTip: 'MATERIAL', produsId: '', magazieId: '', magazieSursaId: '', magazieDestId: '',
  cantitate: '', pretUnitar: '', document: '', dataDocument: '', furnizorId: '', observatii: '',
  motiv: '', produsFinitTintaId: '', lotProductieId: ''
};

export default function Miscari() {
  const [istoric, setIstoric] = useState([]);
  const [magazii, setMagazii] = useState([]);
  const [materiale, setMateriale] = useState([]);
  const [produse, setProduse] = useState([]);
  const [furnizori, setFurnizori] = useState([]);
  const [showModal, setShowModal] = useState(null); // 'intrare' | 'iesire' | 'transfer'
  const [editModal, setEditModal] = useState(null); // randul de miscare in editare
  const [form, setForm] = useState(GOL);
  const [furnizorText, setFurnizorText] = useState('');
  const [produsText, setProdusText] = useState('');
  const [produsOptionsRestranse, setProdusOptionsRestranse] = useState(null);
  const [magazieOptionsRestranse, setMagazieOptionsRestranse] = useState(null);
  const [stocSnapshot, setStocSnapshot] = useState([]); // tot stocul, neagregat - stiu ce are cantitate 0
  const [cantitateMaxima, setCantitateMaxima] = useState(null); // stocul exact al itemului ales, in magazia aleasa
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  function incarcaIstoric() {
    api.get('/api/miscari').then(res => setIstoric(res.data));
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/magazii'), api.get('/api/materiale'), api.get('/api/produse'),
      api.get('/api/furnizori'), api.get('/api/miscari'), api.get('/api/stoc')
    ]).then(([g, m, p, f, mi, s]) => {
      setMagazii(g.data); setMateriale(m.data); setProduse(p.data);
      setFurnizori(f.data); setIstoric(mi.data); setStocSnapshot(s.data);
    }).finally(() => setLoading(false));
  }, []);

  function deschide(tip) {
    setForm({ ...GOL, dataDocument: new Date().toISOString().slice(0, 10) });
    setFurnizorText('');
    setProdusText('');
    setProdusOptionsRestranse(null);
    setMagazieOptionsRestranse(null);
    setShowModal(tip);
  }

  const itemuriToate = form.itemTip === 'MATERIAL' ? materiale : produse;
  // La Iesire/Transfer, un material/produs cu stoc 0 peste tot nu are ce sa iasa
  // sau sa se transfere - nu are sens sa mai apara in lista. La Intrare, ramane
  // vizibil orice (poate fi chiar prima intrare a lui).
  const itemIdsCuStoc = new Set(
    stocSnapshot.filter(s => Number(s.cantitate) > 0).map(s => `${s.itemTip}:${s.itemId}`)
  );
  const itemuriDisponibile = showModal === 'intrare'
    ? itemuriToate
    : itemuriToate.filter(it => itemIdsCuStoc.has(`${form.itemTip}:${it.id}`));
  // Codul e inclus in label ca sa poata fi gasit tastand fie codul, fie denumirea -
  // Combobox filtreaza pe tot textul label-ului. La alegere, campul afiseaza automat
  // "cod - denumire", deci ambele se "completeaza" impreuna.
  const produsOptions = (produsOptionsRestranse ?? itemuriDisponibile).map(it => ({ value: it.id, label: it.cod ? `${it.cod} - ${it.denumire}` : it.denumire }));
  const tipMagazieAsteptat = form.itemTip === 'MATERIAL' ? 'MATERII_PRIME' : 'PRODUSE_FINITE';
  const magaziiDeTipPotrivit = magazii.filter(g => g.tip === tipMagazieAsteptat);
  const magazieOptions = (magazieOptionsRestranse ?? magaziiDeTipPotrivit).map(g => ({ value: g.id, label: `${g.cod ? g.cod + ' - ' : ''}${g.nume}` }));
  const produsFinitTintaOptions = produse.map(p => ({ value: p.id, label: p.cod ? `${p.cod} - ${p.denumire}` : p.denumire }));
  const furnizorOptions = furnizori.map(f => ({ value: f.id, label: f.denumire }));

  // Materialul deja ales, cu tot cu pretul lui curent din nomenclator (daca exista) -
  // la o Intrare, daca materialul are deja un pret salvat, il preluam automat si
  // blocam campul (nu se mai editeaza aici; pretul unui material se schimba doar
  // din Nomenclator materiale, ca sa nu se piarda controlul asupra lui prin
  // introduceri repetate). Doar la un material fara pret inca (nou, sau creat direct
  // din nomenclator fara nicio intrare) campul ramane editabil.
  const materialSelectat = form.itemTip === 'MATERIAL' ? materiale.find(m => String(m.id) === String(form.produsId)) : null;
  const pretBlocat = showModal === 'intrare' && materialSelectat != null && materialSelectat.pretCurent != null;

  async function alegeMagazieSursa(magazieId) {
    setForm(f => ({ ...f, [showModal === 'transfer' ? 'magazieSursaId' : 'magazieId']: magazieId, produsId: '' }));
    if (showModal === 'intrare' || !magazieId) {
      setProdusOptionsRestranse(null);
      return;
    }
    const res = await api.get(`/api/stoc?magazieId=${magazieId}`);
    const filtrate = res.data.filter(s => s.itemTip === form.itemTip);
    setProdusOptionsRestranse(filtrate.map(s => ({ id: s.itemId, denumire: s.denumire })));
  }

  async function alegeProdus(produsId) {
    setForm(f => ({ ...f, produsId }));
    if (showModal === 'intrare' || !produsId) {
      setMagazieOptionsRestranse(null);
      return;
    }
    const res = await api.get(`/api/stoc?itemTip=${form.itemTip}&itemId=${produsId}`);
    const unice = [...new Map(res.data.map(s => [s.magazieId, { id: s.magazieId, nume: s.magazieNume, cod: s.magazieId }])).values()];
    setMagazieOptionsRestranse(unice);
    if (unice.length === 1) {
      const campMagazie = showModal === 'transfer' ? 'magazieSursaId' : 'magazieId';
      setForm(f => ({ ...f, [campMagazie]: unice[0].id }));
    }
  }

  // Comenzile active (nefinalizate) pentru produsul finit tinta ales - un produs
  // poate fi comandat de mai multe ori simultan, deci alegerea comenzii exacte nu
  // se poate deduce automat. Obligatoriu de ales DACA exista cel putin una activa;
  // daca nu exista nicio comanda activa pentru produsul respectiv, se trece mai
  // departe fara (nu are ce alege).
  // Loturile de productie active (in lucru) pentru produsul finit tinta ales -
  // fiecare bucata dintr-o comanda e un lot separat, deci alegerea lotului exact
  // nu se poate deduce automat daca sunt mai multe simultan (bucati/comenzi diferite).
  const [loturiActiveProdus, setLoturiActiveProdus] = useState([]);
  useEffect(() => {
    if (form.motiv !== 'PRODUCTIE' || !form.produsFinitTintaId) {
      setLoturiActiveProdus([]);
      return;
    }
    api.get(`/api/loturi/active-pentru-produs?produsFinitId=${form.produsFinitTintaId}`)
      .then(res => setLoturiActiveProdus(res.data));
  }, [form.motiv, form.produsFinitTintaId]);

  // Cantitatea maxima disponibila pentru itemul si magazia sursa alese - afisata
  // in formular ca reper, la Iesire si Transfer (unde chiar conteaza cat ai voie
  // sa scoti/transferi).
  useEffect(() => {
    const magazieSursa = showModal === 'transfer' ? form.magazieSursaId : form.magazieId;
    if ((showModal !== 'iesire' && showModal !== 'transfer') || !form.produsId || !magazieSursa) {
      setCantitateMaxima(null);
      return;
    }
    api.get(`/api/stoc?itemTip=${form.itemTip}&itemId=${form.produsId}&magazieId=${magazieSursa}`)
      .then(res => setCantitateMaxima(res.data[0]?.cantitate ?? 0));
  }, [showModal, form.itemTip, form.produsId, form.magazieId, form.magazieSursaId]);

  async function rezolvaFurnizor() {
    if (form.furnizorId) return form.furnizorId;
    if (!furnizorText || !furnizorText.trim()) return null;
    const existent = furnizori.find(f => f.denumire.toLowerCase() === furnizorText.trim().toLowerCase());
    if (existent) return existent.id;
    const confirma = window.confirm(`Furnizor nou: "${furnizorText.trim()}" - il adaugi?`);
    if (!confirma) return undefined;
    const res = await api.post('/api/furnizori', { denumire: furnizorText.trim() });
    const listaNoua = await api.get('/api/furnizori');
    setFurnizori(listaNoua.data);
    return res.data.id;
  }

  // Ca la furnizor: daca la o Intrare de material tastezi un nume care nu exista
  // inca in nomenclator, il creezi pe loc (cu confirmare), cu cod automat si
  // pretul din campul de mai jos ca pret initial. Pentru orice alt caz (produs
  // deja ales din lista, sau Iesire/Transfer, unde produsul trebuie sa existe deja
  // ca sa aiba stoc), se comporta ca inainte.
  async function rezolvaProdus() {
    if (form.produsId) return form.produsId;
    if (showModal !== 'intrare' || form.itemTip !== 'MATERIAL') return null;
    if (!produsText || !produsText.trim()) return null;
    const existent = materiale.find(m => m.denumire.toLowerCase() === produsText.trim().toLowerCase());
    if (existent) return existent.id;
    const confirma = window.confirm(`Material nou: "${produsText.trim()}" - il adaugi in nomenclator?`);
    if (!confirma) return undefined;
    const res = await api.post('/api/materiale', { denumire: produsText.trim(), um: 'buc' });
    const listaNoua = await api.get('/api/materiale');
    setMateriale(listaNoua.data);
    return res.data.id;
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;

    // Validare explicita: Combobox-urile (Produs/Magazie) permit sa tastezi orice,
    // dar daca nu s-a ales efectiv o optiune din lista, campul ramane gol - fara
    // aceasta verificare, formularul s-ar trimite oricum si serverul ar respinge
    // cererea cu o eroare neclara. Exceptie: la Intrare de material, un text tastat
    // care nu se potriveste cu nimic e tratat ca material nou (vezi rezolvaProdus).
    const posibilMaterialNou = showModal === 'intrare' && form.itemTip === 'MATERIAL' && produsText.trim();
    if (!form.produsId && !posibilMaterialNou) {
      setToast({ message: 'Alege un produs din lista (nu doar text tastat)', type: 'error' });
      return;
    }
    if ((showModal === 'intrare' || showModal === 'iesire') && !form.magazieId) {
      setToast({ message: 'Alege o magazie din lista', type: 'error' });
      return;
    }
    if (showModal === 'transfer' && (!form.magazieSursaId || !form.magazieDestId)) {
      setToast({ message: 'Alege ambele magazii (sursa si destinatie)', type: 'error' });
      return;
    }
    if (showModal === 'transfer' && String(form.magazieSursaId) === String(form.magazieDestId)) {
      setToast({ message: 'Magazia sursa si cea de destinatie nu pot fi aceeasi', type: 'error' });
      return;
    }
    if (!form.dataDocument) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    if (showModal === 'iesire' && !form.motiv) {
      setToast({ message: 'Alege motivul iesirii', type: 'error' });
      return;
    }
    if (showModal === 'iesire' && form.itemTip === 'MATERIAL' && form.motiv === 'PRODUCTIE' && !form.produsFinitTintaId) {
      setToast({ message: 'Alege produsul finit pentru care se consuma materialul, ca sa se calculeze costul', type: 'error' });
      return;
    }
    if (showModal === 'iesire' && form.motiv === 'PRODUCTIE' && !form.lotProductieId) {
      setToast({ message: loturiActiveProdus.length > 0
        ? 'Alege lotul de productie (bucata) pentru care se consuma acest material'
        : 'Nu exista niciun lot de productie activ pentru acest produs - porneste unul din Comenzi', type: 'error' });
      return;
    }
    const cantitateNormalizata = normalizeazaNumar(form.cantitate);
    if (!cantitateNormalizata || Number(cantitateNormalizata) <= 0) {
      setToast({ message: 'Introdu o cantitate valida', type: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const itemIdRezolvat = await rezolvaProdus();
      if (itemIdRezolvat === undefined) return; // a refuzat crearea materialului nou
      if (!itemIdRezolvat) {
        setToast({ message: 'Alege un produs din lista (nu doar text tastat)', type: 'error' });
        return;
      }

      let furnizorId = null;
      if (showModal === 'intrare') {
        furnizorId = await rezolvaFurnizor();
        if (furnizorId === undefined) return;
      }

      const payload = {
        itemTip: form.itemTip, itemId: itemIdRezolvat, cantitate: cantitateNormalizata,
        document: form.document || null, dataDocument: form.dataDocument || null,
        observatii: form.observatii
      };
      let endpoint = showModal;
      if (showModal === 'intrare') {
        payload.magazieId = form.magazieId;
        payload.furnizorId = furnizorId;
        if (form.itemTip === 'MATERIAL') {
          payload.pretUnitar = pretBlocat ? materialSelectat.pretCurent : normalizeazaNumar(form.pretUnitar);
        }
      } else if (showModal === 'iesire') {
        payload.magazieId = form.magazieId;
        payload.motiv = form.motiv;
        if (form.motiv === 'PRODUCTIE') {
          payload.lotProductieId = form.lotProductieId;
        }
      } else if (showModal === 'transfer') {
        payload.magazieSursaId = form.magazieSursaId;
        payload.magazieDestId = form.magazieDestId;
      }

      await api.post(`/api/miscari/${endpoint}`, payload);
      setToast({ message: 'Miscare inregistrata', type: 'ok' });
      setShowModal(null);
      incarcaIstoric();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la inregistrare', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  function deschideEditare(m) {
    setEditModal({
      id: m.id, itemTip: m.itemTip, itemId: m.itemId, denumireInitiala: m.denumire,
      magazieId: m.magazieSursaId || m.magazieDestId,
      esteSursa: m.magazieSursaId != null,
      magazieSursaId: m.magazieSursaId, magazieDestId: m.magazieDestId,
      cantitate: m.cantitate, furnizorId: m.furnizorId || '',
      document: m.document || '', dataDocument: m.dataDocument || '', observatii: m.observatii || ''
    });
  }

  async function salveazaEditare(e) {
    e.preventDefault();
    if (submitting) return;
    const cantitateNormalizata = normalizeazaNumar(editModal.cantitate);
    if (!cantitateNormalizata || Number(cantitateNormalizata) <= 0) {
      setToast({ message: 'Introdu o cantitate valida', type: 'error' });
      return;
    }
    if (!editModal.dataDocument) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        itemId: editModal.itemId,
        cantitate: cantitateNormalizata,
        document: editModal.document || null,
        dataDocument: editModal.dataDocument || null,
        observatii: editModal.observatii
      };
      if (editModal.magazieSursaId != null && editModal.magazieDestId != null) {
        // TRANSFER - ambele magazii editabile separat
        payload.magazieSursaId = editModal.magazieSursaId;
        payload.magazieDestId = editModal.magazieDestId;
      } else {
        payload.magazieId = editModal.magazieId;
      }
      // Furnizorul are sens doar la o miscare care a fost INTRARE (nu are magazieSursaId).
      if (editModal.magazieSursaId == null) payload.furnizorId = editModal.furnizorId || null;
      await api.put(`/api/miscari/${editModal.id}`, payload);
      setToast({ message: 'Miscare actualizata', type: 'ok' });
      setEditModal(null);
      incarcaIstoric();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la editare', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const coloane = [
    { key: 'dataAfisata', label: 'Data', render: m => m.dataAfisata ? new Date(m.dataAfisata).toLocaleDateString('ro-RO') : '-' },
    { key: 'tip', label: 'Tip' },
    { key: 'magazieSursaNume', label: 'Sursa' },
    { key: 'magazieDestNume', label: 'Destinatie' },
    { key: 'cod', label: 'Cod', render: m => m.cod || '-' },
    { key: 'denumire', label: 'Produs' },
    { key: 'motiv', label: 'Motiv', render: m => m.motiv ? (MOTIV_LABEL[m.motiv] || m.motiv) : '-' },
    { key: 'produsFinitTintaDenumire', label: 'Produs (cost)', render: m => m.produsFinitTintaDenumire || '-' },
    { key: 'cantitate', label: 'Cantitate', render: m => `${formatCantitate(m.cantitate)} ${m.um || ''}` },
    { key: 'pretUnitar', label: 'Pret', render: m => m.pretUnitar != null ? `${formatCantitate(m.pretUnitar)} lei` : '-' },
    { key: 'valoareTotala', label: 'Valoare', render: m => m.valoareTotala != null ? `${formatCantitate(m.valoareTotala)} lei` : '-' },
    { key: 'document', label: 'Document' },
    { key: 'furnizorNume', label: 'Furnizor' },
    { key: 'actiuni', label: '', filterable: false, render: m => (
      <>
        {m.tip === 'IESIRE' && m.motiv === 'PRODUCTIE' && (
          <button type="button" className="btn secondary" onClick={() => deschideBonConsum(m.id)}>Bon de consum</button>
        )}{' '}
        {Number(m.miscariUlterioare) === 0
          ? <button className="btn-edit" onClick={() => deschideEditare(m)}>Editeaza</button>
          : <span style={{ color: '#aaa', fontSize: 12 }}>blocat</span>}
      </>
    ) }
  ];

  // Bonul de consum se deschide ca pagina HTML (A4 landscape) cu buton de Print propriu.
  // Fereastra se deschide acum, direct din click - dupa un await browserul ar considera-o
  // pop-up nesolicitat si ar bloca-o.
  async function deschideBonConsum(id) {
    const fereastra = window.open('', '_blank');
    if (!fereastra) {
      setToast({ message: 'Browserul a blocat fereastra noua - permite ferestrele pop-up pentru acest site', type: 'error' });
      return;
    }
    fereastra.document.write('<p style="font-family:Arial,sans-serif;padding:24px">Se genereaza bonul de consum...</p>');
    try {
      const res = await api.get(`/api/miscari/${id}/bon-consum`);
      fereastra.document.open();
      fereastra.document.write(construiesteHtmlBonConsum(res.data));
      fereastra.document.close();
    } catch (err) {
      fereastra.close();
      setToast({ message: err.response?.data?.error || 'Eroare la generarea bonului de consum', type: 'error' });
    }
  }

  function exportaIstoric() {
    const coloane = [
      { key: 'data', label: 'Data' },
      { key: 'tip', label: 'Tip' },
      { key: 'magazieSursaNume', label: 'Sursa' },
      { key: 'magazieDestNume', label: 'Destinatie' },
      { key: 'cod', label: 'Cod' },
      { key: 'denumire', label: 'Produs' },
      { key: 'motiv', label: 'Motiv' },
      { key: 'produsFinitTintaDenumire', label: 'Produs (cost)' },
      { key: 'cantitate', label: 'Cantitate' },
      { key: 'um', label: 'UM' },
      { key: 'pretUnitar', label: 'Pret' },
      { key: 'valoareTotala', label: 'Valoare' },
      { key: 'document', label: 'Document' },
      { key: 'furnizorNume', label: 'Furnizor' }
    ];
    const randuri = istoric.map(m => ({
      data: m.dataAfisata ? new Date(m.dataAfisata).toLocaleDateString('ro-RO') : '',
      tip: m.tip || '',
      magazieSursaNume: m.magazieSursaNume || '',
      magazieDestNume: m.magazieDestNume || '',
      cod: m.cod || '',
      denumire: m.denumire || '',
      motiv: m.motiv ? (MOTIV_LABEL[m.motiv] || m.motiv) : '',
      produsFinitTintaDenumire: m.produsFinitTintaDenumire || '',
      cantitate: formatNumarRaport(m.cantitate),
      um: m.um || '',
      pretUnitar: formatNumarRaport(m.pretUnitar),
      valoareTotala: formatNumarRaport(m.valoareTotala),
      document: m.document || '',
      furnizorNume: m.furnizorNume || ''
    }));
    exportaCSV('miscari-stoc.csv', coloane, randuri);
  }

  function tipareseIstoric() {
    const coloane = [
      { key: 'data', label: 'Data' },
      { key: 'tip', label: 'Tip' },
      { key: 'produs', label: 'Produs' },
      { key: 'motiv', label: 'Motiv' },
      { key: 'cantitate', label: 'Cantitate', dreapta: true },
      { key: 'pretUnitar', label: 'Pret', dreapta: true },
      { key: 'valoareTotala', label: 'Valoare', dreapta: true },
      { key: 'document', label: 'Document' }
    ];
    const randuri = istoric.map(m => ({
      data: m.dataAfisata ? new Date(m.dataAfisata).toLocaleDateString('ro-RO') : '',
      tip: m.tip || '',
      produs: `${m.cod ? m.cod + ' - ' : ''}${m.denumire || ''}`,
      motiv: m.motiv ? (MOTIV_LABEL[m.motiv] || m.motiv) : '-',
      cantitate: `${formatNumarRaport(m.cantitate)} ${m.um || ''}`,
      pretUnitar: m.pretUnitar != null ? `${formatNumarRaport(m.pretUnitar)} lei` : '-',
      valoareTotala: m.valoareTotala != null ? `${formatNumarRaport(m.valoareTotala)} lei` : '-',
      document: m.document || '-'
    }));
    printeazaTabel('Miscari de stoc', coloane, randuri);
  }

  return (
    <div>
      <h1>Miscari de stoc</h1>
      <div className="toolbar">
        <button className="btn-add" onClick={() => deschide('intrare')}>+ Intrare</button>
        <button className="btn secondary" onClick={() => deschide('iesire')}>- Iesire</button>
        <button className="btn secondary" onClick={() => deschide('transfer')}>Transfer</button>
        <button className="btn-csv" onClick={exportaIstoric}>Export CSV</button>
        <button className="btn-print" onClick={tipareseIstoric}>Print</button>
      </div>

      {loading ? <p>Se incarca...</p> : <FilterableTable columns={coloane} rows={istoric} />}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showModal === 'intrare' ? 'Intrare in stoc' : showModal === 'iesire' ? 'Iesire din stoc' : 'Transfer intre magazii'}</h2>
              <button type="button" className="modal-close" onClick={() => setShowModal(null)}>&times;</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                {showModal === 'intrare' ? (
                  <>
                    <div className="field-row">
                      <div className="field">
                        <label>Tip produs</label>
                        <select value={form.itemTip} onChange={e => { setForm({ ...GOL, itemTip: e.target.value }); setProdusText(''); setProdusOptionsRestranse(null); setMagazieOptionsRestranse(null); }}>
                          <option value="MATERIAL">Materiale</option>
                          <option value="PRODUS_FINIT">Produs finit</option>
                        </select>
                      </div>
                      <div className="field">
                        <label>Magazie *</label>
                        <Combobox options={magazieOptions} value={form.magazieId} placeholder="Alege magazia..."
                          onSelect={(val) => val ? alegeMagazieSursa(val) : setForm(f => ({ ...f, magazieId: '' }))} />
                      </div>
                    </div>
                    <div className="field-row">
                      <div className="field">
                        <label>Produs *</label>
                        <Combobox options={produsOptions} value={form.produsId} placeholder="Tasteaza pentru a cauta sau adauga unul nou..."
                          onSelect={(val) => val ? alegeProdus(val) : setForm(f => ({ ...f, produsId: '' }))}
                          onTextChange={setProdusText} allowFree />
                      </div>
                      <div className="field">
                        <label>Cantitate *</label>
                        <input type="text" inputMode="decimal" value={form.cantitate}
                          onChange={e => setForm({ ...form, cantitate: filtreazaCantitate(e.target.value) })} required />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="field-row">
                      <div className="field">
                        <label>Tip produs</label>
                        <select value={form.itemTip} onChange={e => { setForm({ ...GOL, itemTip: e.target.value }); setProdusText(''); setProdusOptionsRestranse(null); setMagazieOptionsRestranse(null); }}>
                          <option value="MATERIAL">Materiale</option>
                          {showModal === 'transfer' && <option value="PRODUS_FINIT">Produs finit</option>}
                        </select>
                      </div>
                      <div className="field">
                        <label>Produs *</label>
                        <Combobox options={produsOptions} value={form.produsId} placeholder="Tasteaza pentru a cauta..."
                          onSelect={(val) => val ? alegeProdus(val) : setForm(f => ({ ...f, produsId: '' }))} />
                      </div>
                    </div>

                    {showModal === 'iesire' && (
                      <div className="field-row">
                        <div className="field">
                          <label>Motiv *</label>
                          <select value={form.motiv}
                            onChange={e => setForm({ ...form, motiv: e.target.value, produsFinitTintaId: '' })} required>
                            <option value="">Alege...</option>
                            <option value="PRODUCTIE">Transfer catre productie (consum)</option>
                            <option value="CASARE">Casare</option>
                            <option value="PIERDERE">Pierdere</option>
                            <option value="DISTRUGERE">Distrugere</option>
                          </select>
                        </div>
                        {form.itemTip === 'MATERIAL' && form.motiv === 'PRODUCTIE' && (
                          <div className="field">
                            <label>Produs finit (pentru cost) *</label>
                            <Combobox options={produsFinitTintaOptions} value={form.produsFinitTintaId} placeholder="Alege produsul..."
                              onSelect={(val) => setForm(f => ({ ...f, produsFinitTintaId: val || '', lotProductieId: '' }))} />
                          </div>
                        )}
                        {form.itemTip === 'MATERIAL' && form.motiv === 'PRODUCTIE' && form.produsFinitTintaId && (
                          <div className="field">
                            <label>Lot (bucata) *</label>
                            <select value={form.lotProductieId} onChange={e => setForm({ ...form, lotProductieId: e.target.value })} required>
                              <option value="">Alege...</option>
                              {loturiActiveProdus.map(lot => (
                                <option key={lot.id} value={lot.id}>
                                  {lot.client} - {lot.dataStart ? new Date(lot.dataStart).toLocaleDateString('ro-RO') : ''}
                                </option>
                              ))}
                            </select>
                            {loturiActiveProdus.length === 0 && (
                              <span style={{ fontSize: 12, color: '#c0392b' }}>Niciun lot activ - porneste unul din Comenzi.</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {showModal === 'iesire' && (
                      <div className="field-row">
                        <div className="field">
                          <label>Magazie *</label>
                          <Combobox options={magazieOptions} value={form.magazieId} placeholder="Alege magazia..."
                            onSelect={(val) => val ? alegeMagazieSursa(val) : setForm(f => ({ ...f, magazieId: '' }))} />
                        </div>
                        <div className="field">
                          <label>Cantitate *{cantitateMaxima != null && <span style={{ fontWeight: 400, color: '#888' }}> (disponibil: {formatCantitate(cantitateMaxima)})</span>}</label>
                          <Combobox
                            options={cantitateMaxima != null && cantitateMaxima > 0
                              ? Array.from({ length: Math.floor(cantitateMaxima) }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
                              : []}
                            value={form.cantitate} placeholder="Cantitate" allowFree
                            onTextChange={(txt) => setForm(f => ({ ...f, cantitate: filtreazaCantitate(txt) }))}
                            onSelect={(val) => setForm(f => ({ ...f, cantitate: val != null ? String(val) : '' }))} />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {showModal === 'transfer' && (
                  <>
                    <div className="field-row">
                      <div className="field">
                        <label>Din magazia *</label>
                        <Combobox options={magazieOptions} value={form.magazieSursaId} placeholder="Alege magazia sursa..."
                          onSelect={(val) => val ? alegeMagazieSursa(val) : setForm(f => ({ ...f, magazieSursaId: '' }))} />
                      </div>
                      <div className="field">
                        <label>In magazia *</label>
                        <select value={form.magazieDestId} onChange={e => setForm({ ...form, magazieDestId: e.target.value })} required>
                          <option value="">Alege...</option>
                          {magaziiDeTipPotrivit.map(g => <option key={g.id} value={g.id}>{g.cod ? `${g.cod} - ` : ''}{g.nume}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="field">
                      <label>Cantitate *{cantitateMaxima != null && <span style={{ fontWeight: 400, color: '#888' }}> (disponibil: {formatCantitate(cantitateMaxima)})</span>}</label>
                      <Combobox
                        options={cantitateMaxima != null && cantitateMaxima > 0
                          ? Array.from({ length: Math.floor(cantitateMaxima) }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
                          : []}
                        value={form.cantitate} placeholder="Cantitate" allowFree
                        onTextChange={(txt) => setForm(f => ({ ...f, cantitate: filtreazaCantitate(txt) }))}
                        onSelect={(val) => setForm(f => ({ ...f, cantitate: val != null ? String(val) : '' }))} />
                    </div>
                  </>
                )}

                {showModal === 'intrare' && form.itemTip === 'MATERIAL' && (
                  <div className="field-row">
                    <div className="field">
                      <label>Pret unitar *{pretBlocat && <span style={{ fontWeight: 400, color: '#888' }}> (din nomenclator - se schimba doar de acolo)</span>}</label>
                      <input type="text" inputMode="decimal"
                        value={pretBlocat ? materialSelectat.pretCurent : form.pretUnitar}
                        disabled={pretBlocat}
                        onChange={e => setForm({ ...form, pretUnitar: filtreazaCantitate(e.target.value) })} required />
                    </div>
                    <div className="field">
                      <label>Valoare totala</label>
                      <input type="text" disabled
                        value={(() => {
                          const pret = pretBlocat ? materialSelectat.pretCurent : normalizeazaNumar(form.pretUnitar);
                          return pret && form.cantitate ? formatCantitate(Number(pret) * Number(normalizeazaNumar(form.cantitate))) : '';
                        })()} />
                    </div>
                  </div>
                )}

                <div className="field-row">
                  <div className="field">
                    <label>Document (nr. factura/aviz)</label>
                    <input value={form.document} onChange={e => setForm({ ...form, document: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Data *</label>
                    <input type="date" value={form.dataDocument} onChange={e => setForm({ ...form, dataDocument: e.target.value })} required />
                  </div>
                </div>

                {showModal === 'intrare' && (
                  <div className="field">
                    <label>Furnizor</label>
                    <Combobox options={furnizorOptions} value={form.furnizorId} placeholder="Tasteaza numele furnizorului..."
                      onSelect={(val) => setForm(f => ({ ...f, furnizorId: val || '' }))}
                      onTextChange={setFurnizorText} allowFree />
                  </div>
                )}

                <div className="field">
                  <label>Observatii</label>
                  <input value={form.observatii} onChange={e => setForm({ ...form, observatii: e.target.value })} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setShowModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Se salveaza...' : 'Salveaza'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editeaza miscare - {editModal.denumireInitiala}</h2>
              <button type="button" className="modal-close" onClick={() => setEditModal(null)}>&times;</button>
            </div>
            <form onSubmit={salveazaEditare}>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: '#888', marginTop: 0 }}>
                  Editabil doar cat timp nu exista o iesire/transfer ulterioara pe acest produs.
                </p>
                <div className="field-row">
                  <div className="field">
                    <label>Produs</label>
                    <select value={editModal.itemId} onChange={e => setEditModal({ ...editModal, itemId: Number(e.target.value) })}>
                      {(editModal.itemTip === 'MATERIAL' ? materiale : produse).map(it => (
                        <option key={it.id} value={it.id}>{it.denumire}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Cantitate *</label>
                    <input type="text" inputMode="decimal" value={editModal.cantitate}
                      onChange={e => setEditModal({ ...editModal, cantitate: filtreazaCantitate(e.target.value) })} required />
                  </div>
                </div>

                {((editModal.esteSursa && editModal.magazieDestId == null) || (!editModal.esteSursa && editModal.magazieSursaId == null)) && (
                  <div className="field">
                    <label>Magazie</label>
                    <select value={editModal.magazieId} onChange={e => setEditModal({ ...editModal, magazieId: Number(e.target.value) })}>
                      {magazii.filter(g => g.tip === (editModal.itemTip === 'MATERIAL' ? 'MATERII_PRIME' : 'PRODUSE_FINITE')).map(g => (
                        <option key={g.id} value={g.id}>{g.cod} - {g.nume}</option>
                      ))}
                    </select>
                  </div>
                )}
                {editModal.magazieSursaId != null && editModal.magazieDestId != null && (
                  <div className="field-row">
                    <div className="field">
                      <label>Din magazia</label>
                      <select value={editModal.magazieSursaId} onChange={e => setEditModal({ ...editModal, magazieSursaId: Number(e.target.value) })}>
                        {magazii.filter(g => g.tip === (editModal.itemTip === 'MATERIAL' ? 'MATERII_PRIME' : 'PRODUSE_FINITE')).map(g => (
                          <option key={g.id} value={g.id}>{g.cod} - {g.nume}</option>
                        ))}
                      </select>
                    </div>
                    <div className="field">
                      <label>In magazia</label>
                      <select value={editModal.magazieDestId} onChange={e => setEditModal({ ...editModal, magazieDestId: Number(e.target.value) })}>
                        {magazii.filter(g => g.tip === (editModal.itemTip === 'MATERIAL' ? 'MATERII_PRIME' : 'PRODUSE_FINITE')).map(g => (
                          <option key={g.id} value={g.id}>{g.cod} - {g.nume}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="field-row">
                  <div className="field">
                    <label>Document</label>
                    <input value={editModal.document} onChange={e => setEditModal({ ...editModal, document: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Data *</label>
                    <input type="date" value={editModal.dataDocument} onChange={e => setEditModal({ ...editModal, dataDocument: e.target.value })} required />
                  </div>
                </div>

                {editModal.magazieSursaId == null && (
                  <div className="field">
                    <label>Furnizor</label>
                    <Combobox options={furnizorOptions} value={editModal.furnizorId} placeholder="Tasteaza numele furnizorului..."
                      onSelect={(val) => setEditModal(f => ({ ...f, furnizorId: val || '' }))} />
                  </div>
                )}

                <div className="field">
                  <label>Observatii</label>
                  <input value={editModal.observatii} onChange={e => setEditModal({ ...editModal, observatii: e.target.value })} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setEditModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Se salveaza...' : 'Salveaza'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}
